/**
 * Template details and the author's unsaved edits, per template row.
 *
 * Unsaved edits live in memory only (never browser storage) and survive
 * switching rows, channels and tabs; they are dropped only by Discard, by a
 * successful save, or by leaving the screen after confirming. Every write names
 * the revision it was based on; a conflict keeps the edits and reloads the row
 * so the author can decide. A response that arrives for a row the author has
 * left is still applied to that row's own state (never to the visible one).
 */
import { useCallback, useRef, useState } from "react";
import {
  messageTemplateDraftGuard,
  type MessageTemplateDetail,
  type MessageTemplatePublication,
  type MessageTemplateResetResult,
  type MessageBuilderWriteWarning,
} from "@v0y4ge/api-contracts";
import { builderApi, classifyFailure, type Failure } from "./api";
import { runIntent, type CommandResult } from "./commands";
import { baseWork, sameWork, toInput, type Work } from "./model";

/** An author's optional version note (contract: trimmed, at most 200 characters). Absent keeps the current note. */
type Note = string;

export type DetailState =
  | { phase: "loading" }
  | { phase: "error"; failure: Failure }
  | { phase: "ready"; detail: MessageTemplateDetail; refreshing: boolean };

export type Busy = "save" | "publish" | "discard" | "customize" | "reset" | null;

export interface TemplateStore {
  detailOf(id: string): DetailState | undefined;
  load(id: string, force?: boolean): void;
  workOf(id: string): Work | null;
  setWork(id: string, next: Work): void;
  dirty(id: string): boolean;
  busy(id: string): Busy;
  warnings(id: string): MessageBuilderWriteWarning[];
  discardLocal(id: string): void;
  save(id: string, note?: Note): Promise<CommandResult<MessageTemplateDetail>>;
  publish(id: string, note?: Note): Promise<CommandResult<{ detail: MessageTemplateDetail; publication: MessageTemplatePublication; savedFirst: boolean }>>;
  discardDraft(id: string): Promise<CommandResult<MessageTemplateDetail>>;
  /** Create this company's draft of a platform message (both channels server-side; the caller reloads the sibling row). */
  customize(id: string): Promise<CommandResult<MessageTemplateDetail>>;
  /** Reset a customised message to its default, as the row's `resettable` read states. */
  reset(id: string): Promise<CommandResult<{ detail: MessageTemplateDetail; reset: MessageTemplateResetResult }>>;
  replaceDetail(id: string, detail: MessageTemplateDetail): void;
  anyDirty(): boolean;
  /**
   * Revision conflicts in a row since the last successful command. The first conflict reloads the row
   * (the author's input is kept) so the next try is built from the newest guard. A second conflict on a
   * freshly reloaded row means reloading cannot help (for example a draft stranded by a shared appointment
   * source that moved after the draft was created): the store stops reloading and marks it `stranded`.
   */
  conflictState(id: string): { count: number; stranded: boolean };
  /** The author asked to try again from the server's current state (one reload, never a loop). */
  retryFromServer(id: string): void;
}

/**
 * The save body. The guard (draft revision + generation, base asset id + version)
 * comes only from the row the server returned, through the contract's own helper,
 * so a command prepared for a discarded draft can never act on a new one.
 */
function saveInput(detail: MessageTemplateDetail, work: Work, note?: Note) {
  return { ...messageTemplateDraftGuard(detail), content: toInput(work), ...(note ? { note } : {}) };
}
function commandInput(detail: MessageTemplateDetail, note?: Note) {
  return { ...messageTemplateDraftGuard(detail), ...(note ? { note } : {}) };
}

export function useTemplateStore(): TemplateStore {
  const details = useRef(new Map<string, DetailState>());
  const works = useRef(new Map<string, Work>());
  const busyMap = useRef(new Map<string, Busy>());
  const warn = useRef(new Map<string, MessageBuilderWriteWarning[]>());
  const loads = useRef(new Map<string, number>());
  const conflicts = useRef(new Map<string, number>());
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => n + 1), []);

  const replaceDetail = useCallback((id: string, detail: MessageTemplateDetail) => {
    details.current.set(id, { phase: "ready", detail, refreshing: false });
    const w = works.current.get(id);
    if (w && sameWork(w, baseWork(detail))) works.current.delete(id);
    bump();
  }, [bump]);

  const load = useCallback((id: string, force = false) => {
    const cur = details.current.get(id);
    if (cur && !force && cur.phase !== "error") return;
    const mine = (loads.current.get(id) || 0) + 1;
    loads.current.set(id, mine);
    details.current.set(id, cur && cur.phase === "ready" ? { ...cur, refreshing: true } : { phase: "loading" });
    bump();
    builderApi.getMessageTemplate(id).then(
      (res) => { if (loads.current.get(id) === mine) replaceDetail(id, res.data); },
      (error: unknown) => {
        if (loads.current.get(id) !== mine) return;
        const prev = details.current.get(id);
        details.current.set(id, prev && prev.phase === "ready" ? { ...prev, refreshing: false } : { phase: "error", failure: classifyFailure(error) });
        bump();
      },
    );
  }, [bump, replaceDetail]);

  const readyDetail = (id: string): MessageTemplateDetail | null => {
    const d = details.current.get(id);
    return d && d.phase === "ready" ? d.detail : null;
  };

  const workOf = (id: string): Work | null => {
    const w = works.current.get(id);
    if (w) return w;
    const d = readyDetail(id);
    return d ? baseWork(d) : null;
  };

  const setWork = (id: string, next: Work) => {
    const d = readyDetail(id);
    if (d && sameWork(next, baseWork(d))) works.current.delete(id); else works.current.set(id, next);
    warn.current.delete(id);
    bump();
  };

  const dirty = (id: string) => works.current.has(id);

  const withBusy = async <T,>(id: string, kind: Busy, fn: () => Promise<T>): Promise<T> => {
    busyMap.current.set(id, kind); bump();
    try { return await fn(); } finally { busyMap.current.delete(id); bump(); }
  };

  /** After every command: a success clears the conflict count; a conflict reloads once, then stops. */
  const settle = <T,>(id: string, r: CommandResult<T>): CommandResult<T> => {
    if (r.ok) { conflicts.current.delete(id); return r; }
    if (r.failure.kind !== "conflict") return r;
    const n = (conflicts.current.get(id) || 0) + 1;
    conflicts.current.set(id, n);
    if (n === 1) load(id, true);
    bump();
    return r;
  };

  const save = (id: string, note?: Note) => withBusy(id, "save", async (): Promise<CommandResult<MessageTemplateDetail>> => {
    const d = readyDetail(id); const w = works.current.get(id);
    if (!d || !w) return { ok: false, failure: { kind: "failed", code: "", status: 0, message: "", fields: [], params: {} } };
    const body = saveInput(d, w, note);
    const r = await runIntent(`template:${id}:save`, body, (key) => builderApi.saveMessageTemplateDraft(id, body, { idempotencyKey: key }));
    if (r.ok) {
      warn.current.set(id, r.value.warnings || []);
      // Keep edits typed while the save was in flight.
      const latest = works.current.get(id);
      details.current.set(id, { phase: "ready", detail: r.value.data, refreshing: false });
      if (!latest || sameWork(latest, w) || sameWork(latest, baseWork(r.value.data))) works.current.delete(id);
      bump();
      return settle(id, { ok: true, value: r.value.data });
    }
    return settle(id, r);
  });

  type Published = { detail: MessageTemplateDetail; publication: MessageTemplatePublication; savedFirst: boolean };
  const publish = (id: string, note?: Note) => withBusy(id, "publish", async (): Promise<CommandResult<Published>> => {
    let savedFirst = false;
    if (works.current.has(id)) {
      const d0 = readyDetail(id); const w = works.current.get(id) as Work;
      if (!d0) return { ok: false as const, failure: classifyFailure(null) };
      const body = saveInput(d0, w, note);
      const s = await runIntent(`template:${id}:save`, body, (key) => builderApi.saveMessageTemplateDraft(id, body, { idempotencyKey: key }));
      if (!s.ok) { settle(id, s); return { ok: false as const, failure: s.failure }; }
      details.current.set(id, { phase: "ready", detail: s.value.data, refreshing: false });
      if (sameWork(works.current.get(id), w)) works.current.delete(id);
      savedFirst = true;
      bump();
    }
    const d = readyDetail(id);
    if (!d || !d.draft) return { ok: false as const, failure: { kind: "failed" as const, code: "NO_DRAFT", status: 0, message: "", fields: [], params: {} } };
    // A draft customized from a default-workspace message takes over its group: confirm the group as read.
    const body = { ...commandInput(d, note), ...(d.takeoverGroup ? { expectedTakeoverGroupDigest: d.takeoverGroup.digest } : {}) };
    const r = await runIntent(`template:${id}:publish`, body, (key) => builderApi.publishMessageTemplate(id, body, { idempotencyKey: key }));
    if (r.ok) {
      warn.current.set(id, r.value.warnings || []);
      details.current.set(id, { phase: "ready", detail: r.value.data, refreshing: false });
      bump();
      return settle<Published>(id, { ok: true, value: { detail: r.value.data, publication: r.value.publication, savedFirst } });
    }
    settle(id, r);
    return { ok: false, failure: r.failure };
  });

  const discardDraft = (id: string) => withBusy(id, "discard", async (): Promise<CommandResult<MessageTemplateDetail>> => {
    const d = readyDetail(id);
    if (!d || !d.draft) return { ok: false, failure: { kind: "failed", code: "NO_DRAFT", status: 0, message: "", fields: [], params: {} } };
    const body = commandInput(d);
    const r = await runIntent(`template:${id}:discard`, body, (key) => builderApi.discardMessageTemplateDraft(id, body, { idempotencyKey: key }));
    if (r.ok) { works.current.delete(id); replaceDetail(id, r.value.data); return settle(id, { ok: true, value: r.value.data }); }
    return settle(id, r);
  });

  const customize = (id: string) => withBusy(id, "customize", async (): Promise<CommandResult<MessageTemplateDetail>> => {
    const d = readyDetail(id);
    if (!d) return { ok: false, failure: { kind: "failed", code: "", status: 0, message: "", fields: [], params: {} } };
    // The base guard is the published asset the author saw, and the authoring revision as read.
    const body = { expectedBaseAssetId: d.published?.assetId ?? null, expectedBaseAssetVersion: d.published?.version ?? null, expectedAuthoringRevision: d.authoringRevision };
    const r = await runIntent(`template:${id}:customize`, body, (key) => builderApi.customizeMessageTemplate(id, body, { idempotencyKey: key }));
    if (r.ok) { warn.current.set(id, r.value.warnings || []); replaceDetail(id, r.value.data); return settle(id, { ok: true, value: r.value.data }); }
    return settle(id, r);
  });

  const reset = (id: string) => withBusy(id, "reset", async (): Promise<CommandResult<{ detail: MessageTemplateDetail; reset: MessageTemplateResetResult }>> => {
    const d = readyDetail(id);
    if (!d || !d.resettable) return { ok: false, failure: { kind: "failed", code: "", status: 0, message: "", fields: [], params: {} } };
    const body = { expectedResetDigest: d.resettable.digest };
    const r = await runIntent(`template:${id}:reset`, body, (key) => builderApi.resetMessageTemplate(id, body, { idempotencyKey: key }));
    if (r.ok) {
      // The reset discarded the drafts of every row it names, not only this one: drop their unsaved work too, so
      // the sibling rows the editor reloads show the restored content instead of edits over a draft that is gone.
      for (const changed of r.value.reset.templateIds) { works.current.delete(changed); warn.current.delete(changed); }
      works.current.delete(id); warn.current.set(id, r.value.warnings || []);
      replaceDetail(id, r.value.data);
      return settle(id, { ok: true, value: { detail: r.value.data, reset: r.value.reset } });
    }
    return settle(id, r);
  });

  return {
    detailOf: (id) => details.current.get(id),
    load,
    workOf,
    setWork,
    dirty,
    busy: (id) => busyMap.current.get(id) ?? null,
    warnings: (id) => warn.current.get(id) || [],
    discardLocal: (id) => { works.current.delete(id); warn.current.delete(id); bump(); },
    save,
    publish,
    discardDraft,
    customize,
    reset,
    replaceDetail,
    anyDirty: () => works.current.size > 0 || [...busyMap.current.values()].some(Boolean),
    conflictState: (id) => { const count = conflicts.current.get(id) || 0; return { count, stranded: count >= 2 }; },
    retryFromServer: (id) => { conflicts.current.delete(id); load(id, true); },
  };
}
